import { createSelector } from 'reselect'
import { numFormat } from 'src/localization/Num'
import { TeamSize } from 'src/models/config.models'
import { RootState } from 'src/redux/createRootReducer'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'

export const selectSupportedLocalesFromState = (state: RootState): string[] =>
  state.session.config.supportedLocales

export const selectSupportedCurrenciesFromState = (
  state: RootState,
): string[] => state.session.config.supportedCurrencies

export const selectSupportedIndustriesFromState = (
  state: RootState,
): string[] => state.session.config.supportedIndustries

export const selectSupportedIndustriesLocalizedFromState = (
  state: RootState,
): string[] => state.session.config.supportedIndustriesLocalized

export const selectDepartmentOptionsLocalizedFromState = (
  state: RootState,
): string[] => state.session.config.departmentOptionsLocalized

export const selectReferralSourceOptionsLocalizedFromState = (
  state: RootState,
): string[] => state.session.config.referralSourceOptionsLocalized

export const selectTeamSizeOptionsFromState = (state: RootState): TeamSize[] =>
  state.session.config.teamSizeOptions

export const selectIndustryOptions = createSelector(
  [selectSupportedIndustriesLocalizedFromState],
  (localizedIndustries) => {
    const mappedIndustries = localizedIndustries.map((industry) => {
      return {
        value: Object.keys(industry)[0],
        details: {
          text: Object.values(industry)[0],
        },
      }
    })

    const other = mappedIndustries.filter(
      (industry) => industry?.value === 'Other',
    )[0]

    const sorted = mappedIndustries
      .sort((a, b) =>
        a.details.text > b.details.text
          ? 1
          : b.details.text > a.details.text
            ? -1
            : 0,
      )
      .filter((industry) => industry?.value !== 'Other')

    if (other) sorted.push(other)

    return sorted
  },
)

export const selectDepartmentOptions = createSelector(
  [selectDepartmentOptionsLocalizedFromState],
  (localizedDepartments) => {
    const mappedDepartments = localizedDepartments.map((department) => {
      return {
        value: Object.keys(department)[0],
        details: {
          text: Object.values(department)[0],
        },
      }
    })

    const other = mappedDepartments.filter(
      (department) => department?.value === 'Other',
    )[0]

    const sorted = mappedDepartments
      .sort((a, b) =>
        a.details.text > b.details.text
          ? 1
          : b.details.text > a.details.text
            ? -1
            : 0,
      )
      .filter((department) => department?.value !== 'Other')

    if (other) sorted.push(other)

    return sorted
  },
)

export const selectTeamSizeOptions = createSelector(
  [selectTeamSizeOptionsFromState, selectNullSafeLocaleFromState],
  (teamSizes, locale) => {
    const teamSizeText = (teamSize: TeamSize): string =>
      teamSize.max
        ? `${numFormat(locale, teamSize.min)} - ${numFormat(locale, teamSize.max)}`
        : `${numFormat(locale, teamSize.min)}+`

    return teamSizes.map((teamSize) => {
      return {
        value: teamSizeText(teamSize),
        details: {
          text: teamSizeText(teamSize),
          min: teamSize.min,
          max: teamSize.max,
        },
      }
    })
  },
)

export const selectReferralSourceOptions = createSelector(
  [selectReferralSourceOptionsLocalizedFromState],
  (localizedOptions) => {
    return localizedOptions
      ? localizedOptions?.map((option) => ({
          value: Object.keys(option)[0],
          details: {
            text: Object.values(option)[0],
          },
        }))
      : []
  },
)
